import { AspectRatio, Box, Text } from '@chakra-ui/react';
import MuxPlayer from '@mux/mux-player-react';
import React, { useCallback } from 'react';
import { TVideoSource } from '@/modules/content/creation/model/types';
import { LANDSCAPE_RATIO, readElementRatio } from '@/modules/content/common/videoOrientation';
import StatusPoster from '@/modules/content/common/StatusPoster';
import YouTubeVideo from '@/modules/content/common/YouTubeVideo';

type Props = {
  source: TVideoSource;
  youtubeVideoId?: string | null;
  muxStatus?: string | null;
  muxPlaybackId?: string | null;
  posterUrl?: string | null;
  title?: string;
  // A freshly-picked file not yet uploaded/processed — never play a stale asset.
  pendingUpload?: boolean;
  // Shown when source is youtube but no valid video id is available yet.
  emptyHint?: string;
  // width / height. Portrait uploads and YouTube Shorts are not 16:9, and forcing them
  // into a landscape box letterboxes them badly — so the frame follows the video.
  aspectRatio?: number;
  // Fill the container instead of sizing to `aspectRatio`. For layouts that have already
  // committed to a frame (the loop's portrait/landscape variants).
  fillContainer?: boolean;
  // Reports the video's true dimensions once the player knows them, so a caller that
  // guessed the aspect can correct itself. Mux only — a YouTube iframe never tells us.
  onRatioDetected?: (ratio: number) => void;
  // Real play/pause transitions. Supplying this switches the YouTube branch from a bare
  // embed to the IFrame API, which is the only way to observe playback there — so pass it
  // only when playback actually needs to be measured (view telemetry), not by default.
  onPlayStateChange?: (isPlaying: boolean) => void;
  // Playback position, for completion percentage.
  onProgress?: (currentTimeSeconds: number, durationSeconds: number) => void;
};

// Shared video renderer: plays YouTube (embed) and ready Mux assets (Mux player),
// and falls back to a poster + status while a Mux asset is still processing.
const VideoPlayer = ({
  source,
  youtubeVideoId,
  muxStatus,
  muxPlaybackId,
  posterUrl,
  title,
  pendingUpload,
  emptyHint,
  aspectRatio = LANDSCAPE_RATIO,
  fillContainer = false,
  onRatioDetected,
  onPlayStateChange,
  onProgress,
}: Props) => {
  const reportsPlayback = Boolean(onPlayStateChange || onProgress);

  const handleTimeUpdate = useCallback(
    (event: React.SyntheticEvent<HTMLElement> | Event) => {
      if (!onProgress) return;
      const media = event.target as { currentTime?: number; duration?: number } | null;
      if (typeof media?.currentTime === 'number' && typeof media?.duration === 'number' && media.duration > 0) {
        onProgress(media.currentTime, media.duration);
      }
    },
    [onProgress],
  );
  // Mux proxies the underlying media element's properties, so once metadata has loaded
  // the true dimensions are readable — the authoritative answer, replacing any guess.
  const handleLoadedMetadata = useCallback(
    (event: React.SyntheticEvent<HTMLElement> | Event) => {
      if (!onRatioDetected) return;
      const ratio = readElementRatio(event.target);
      if (ratio) onRatioDetected(ratio);
    },
    [onRatioDetected],
  );

  if (source === 'youtube') {
    if (!youtubeVideoId) {
      return (
        <Text color="text.mediumGray" fontSize="14px">
          {emptyHint ?? 'No video yet.'}
        </Text>
      );
    }

    // The bare embed reports nothing about playback. When a caller needs to know whether
    // the video actually played, mount the IFrame API player instead.
    const iframe = reportsPlayback ? (
      <YouTubeVideo
        videoId={youtubeVideoId}
        title={title}
        onPlayStateChange={onPlayStateChange}
        onProgress={onProgress}
      />
    ) : (
      <Box
        as="iframe"
        src={`https://www.youtube.com/embed/${youtubeVideoId}`}
        allowFullScreen
        width="100%"
        height="100%"
        border="none"
      />
    );

    return fillContainer ? (
      <Box width="100%" height="100%" borderRadius="12px" overflow="hidden">
        {iframe}
      </Box>
    ) : (
      <AspectRatio ratio={aspectRatio} width="100%" borderRadius="12px" overflow="hidden">
        {iframe}
      </AspectRatio>
    );
  }

  if (!pendingUpload && muxStatus === 'ready' && muxPlaybackId) {
    return (
      <Box width="100%" height={fillContainer ? '100%' : undefined} borderRadius="12px" overflow="hidden">
        <MuxPlayer
          playbackId={muxPlaybackId}
          streamType="on-demand"
          accentColor="#4E1AEF"
          metadata={{ video_title: title }}
          onLoadedMetadata={handleLoadedMetadata}
          onPlay={() => onPlayStateChange?.(true)}
          onPause={() => onPlayStateChange?.(false)}
          onEnded={() => onPlayStateChange?.(false)}
          onWaiting={() => onPlayStateChange?.(false)}
          onPlaying={() => onPlayStateChange?.(true)}
          onTimeUpdate={handleTimeUpdate}
          style={
            fillContainer
              ? { width: '100%', height: '100%', '--media-object-fit': 'contain' }
              : { width: '100%', aspectRatio: String(aspectRatio) }
          }
        />
      </Box>
    );
  }

  const label = pendingUpload
    ? 'New video — available after processing'
    : muxStatus === 'ready'
      ? 'Mux video'
      : `Mux video · ${muxStatus ?? 'processing'}`;
  return <StatusPoster posterUrl={posterUrl} label={label} />;
};

export default VideoPlayer;
